/**
 * The records table's small pieces, each drawn the one way the design draws
 * it (`Led AI - Records Table.dc.html`) so the table, the list, the board and
 * the New-record modal cannot disagree about what a status or a priority
 * looks like.
 */

import type { CSSProperties, ReactNode } from "react";
import { DsIcon } from "../../components/DsIcon";
import { initialsOf } from "../../components/chrome";
import { DS, FONT_MONO, RT } from "../../tokens";
import { avatarTone, companyTone, type Priority, type StatusDef } from "./recordModel";

/** The design's 14px status glyph: a ring, dashed for a draft, filled with a
 *  pie as work progresses, a dot when ready to close, solid with a check or a
 *  cross when it is closed or rejected. */
export function StatusGlyph({ status, size = 14, style }: { status: Pick<StatusDef, "color" | "glyph">; size?: number; style?: CSSProperties }) {
  const { color, glyph } = status;
  const solid = glyph === "check" || glyph === "x";
  const pie = glyph === "half" ? "50%" : glyph === "threeq" ? "75%" : null;
  return (
    <span
      aria-hidden
      data-glyph={glyph}
      style={{
        flex: "none",
        position: "relative",
        width: size,
        height: size,
        borderRadius: size / 2,
        boxSizing: "border-box",
        border: `1.5px ${glyph === "dashed" ? "dashed" : "solid"} ${color}`,
        background: solid ? color : "transparent",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        ...style,
      }}
    >
      {glyph === "check" && <DsIcon name="check" size={9} color={RT.pane} />}
      {glyph === "x" && <DsIcon name="close" size={8} color={RT.pane} />}
      {glyph === "ringdot" && <span style={{ width: 6, height: 6, borderRadius: 3, background: color, display: "block" }} />}
      {pie && (
        <span
          style={{
            position: "absolute",
            inset: 2,
            borderRadius: "50%",
            background: `conic-gradient(${color} ${pie}, transparent 0)`,
            display: "block",
          }}
        />
      )}
    </span>
  );
}

const PRIORITY_INK: Record<Priority, string> = {
  high: RT.priority.high,
  medium: RT.priority.medium,
  low: RT.priority.low,
};

/** Three bars, lit from the left: Low lights one, Medium two, High three. */
export function PriorityBars({ priority, tall = 12 }: { priority: Priority | undefined; tall?: number }) {
  const ink = priority ? PRIORITY_INK[priority] : RT.barNone;
  const lit = priority === "high" ? 3 : priority === "medium" ? 2 : priority === "low" ? 1 : 3;
  const off = priority ? RT.barOff : RT.barNone;
  const heights = [5, 8, tall];
  return (
    <span aria-hidden style={{ flex: "none", display: "inline-flex", alignItems: "flex-end", gap: 1.5, height: tall }}>
      {heights.map((height, index) => (
        <span key={index} style={{ width: 3, height, borderRadius: 1, background: index < lit ? ink : off, display: "block" }} />
      ))}
    </span>
  );
}

/** A person's initials on their colour. */
export function PersonAvatar({ name, size = 20 }: { name: string; size?: number }) {
  const tone = avatarTone(name);
  return (
    <span
      aria-hidden
      style={{
        flex: "none",
        width: size,
        height: size,
        borderRadius: size / 2,
        background: tone.bg,
        color: tone.fg,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: size >= 22 ? 8 : size >= 20 ? 8 : 7.5,
        fontWeight: 800,
      }}
    >
      {initialsOf(name)}
    </span>
  );
}

/** The dashed ring where an assignee would be. */
export function NoAvatar({ size = 22 }: { size?: number }) {
  return (
    <span
      title="Unassigned"
      style={{ flex: "none", width: size, height: size, borderRadius: size / 2, border: "1.5px dashed rgba(10,10,10,0.25)", display: "block", boxSizing: "border-box" }}
    />
  );
}

/** A subcontractor's square swatch. */
export function CompanySwatch({ name, size = 7, radius = 2 }: { name: string; size?: number; radius?: number }) {
  return <span aria-hidden style={{ flex: "none", width: size, height: size, borderRadius: radius, background: companyTone(name).fill, display: "block" }} />;
}

/** The design's switch: 30 × 18, ink when on. */
export function Toggle({ on, label, onToggle, testId }: { on: boolean; label: string; onToggle: () => void; testId?: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      data-testid={testId}
      onClick={onToggle}
      style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", whiteSpace: "nowrap", border: "none", background: "transparent", padding: 0, font: "inherit" }}
    >
      <span style={{ position: "relative", width: 30, height: 18, borderRadius: 9, background: on ? DS.ink : RT.toggleOff, display: "inline-block", transition: "background .15s" }}>
        <span
          style={{
            position: "absolute",
            top: 2,
            left: on ? 14 : 2,
            width: 14,
            height: 14,
            borderRadius: 7,
            background: RT.pane,
            boxShadow: "0 1px 2px rgba(10,10,10,0.2)",
            transition: "left .15s",
            display: "block",
          }}
        />
      </span>
      <span style={{ fontSize: 13, color: RT.cell }}>{label}</span>
    </button>
  );
}

/** A floating menu under its trigger. */
export function Popover({ children, width, left = 0, right, top = 36, z = 30, testId, style }: {
  children: ReactNode;
  width?: number;
  left?: number;
  right?: number;
  top?: number;
  z?: number;
  testId?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      className="rt-fade"
      data-testid={testId}
      onClick={(event) => event.stopPropagation()}
      style={{
        position: "absolute",
        ...(right !== undefined ? { right } : { left }),
        top,
        zIndex: z,
        width,
        background: RT.pane,
        border: "1px solid rgba(10,10,10,0.12)",
        borderRadius: 12,
        boxShadow: RT.shadowMenu,
        padding: 6,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/** A menu row: icon, label, and whatever trails it. */
export function MenuRow({ icon, children, trailing, onPick, selected, testId, height = 34 }: {
  icon?: ReactNode;
  children: ReactNode;
  trailing?: ReactNode;
  onPick: () => void;
  selected?: boolean;
  testId?: string;
  height?: number;
}) {
  return (
    <button
      type="button"
      className="rt-hover"
      data-testid={testId}
      onClick={(event) => {
        event.stopPropagation();
        onPick();
      }}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 10,
        height,
        padding: "0 10px",
        borderRadius: 8,
        border: "none",
        background: selected ? RT.hover : "transparent",
        font: "inherit",
        fontSize: 13.5,
        fontWeight: selected ? 700 : 500,
        color: DS.ink,
        cursor: "pointer",
        textAlign: "left",
        whiteSpace: "nowrap",
      }}
    >
      {icon}
      <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{children}</span>
      {trailing}
    </button>
  );
}

/** The mono eyebrow over a menu. */
export function MenuEyebrow({ children }: { children: ReactNode }) {
  return <div className="rt-eyebrow" style={EYEBROW_STYLE}>{children}</div>;
}

export const EYEBROW_STYLE: CSSProperties = {
  fontFamily: FONT_MONO,
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: 1.5,
  color: DS.meta,
  textTransform: "uppercase",
  padding: "6px 10px 4px",
};
